import React, { useRef, useState, useCallback } from 'react';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import './SelectInputStyles.scss';
import { useDispatch } from 'react-redux';
import { Icons } from '../../base/BootstrapComponents/BootstrapComponents';
import Dropdown from '../../base/Dropdown/Dropdown';
import TextInput from '../TextInput/TextInput';
import renderMessage from '../renderMessage';
import { required } from '../../../utils/validation';
import CheckBoxInputWithLabel from '../CheckBoxInput/CheckBoxInputWithLabel';
import { errorNotification } from '../../../modules/Notifications/notificationDecorator';
import { getResponseError } from '../../../utils/utils';
import { LOADING_DELAY } from '../../../configs';

export default function MultipleSelectInputWithAsyncHelper(props) {
  const {
    inputProps = null,
    className = '',
    fieldState = {
      error: '',
      touched: false,
    },
    variant = 'default',
    label,
    multiple = false,
    disabled = false,
    dtoObject,
    customValidator = [],
    defaultOptions = [],
    loadAction,
    optionFormatter = null,
  } = props;
  const {
    error,
    isTouched: touched,
  } = fieldState;
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const [toggleFunction, setToggleFunction] = useState(() => () => {});
  const [defaults, setDefaults] = useState(defaultOptions);
  const [options, setOptions] = useState([]);
  const [search, setSearch] = useState('');
  const [timer, setTimer] = useState(null);
  const anchorElement = useRef(null);
  const inputRef = useRef(null);
  const isError = Boolean(error);
  const value = inputProps && (inputProps.value || typeof inputProps.value === 'boolean') ? inputProps.value : [];
  const onChange = item => {
    if (inputProps && inputProps.onChange) {
      const newValue = [...value];
      const index = value.indexOf(item.value);
      if (index > -1) {
        const removed = newValue.splice(index, 1);
        const newDefs = [...defaults].filter(f => f.value !== removed[0]);
        setDefaults(newDefs);
      } else {
        newValue.push(item.value);
        setDefaults([...defaults, item]);
      }
      setSearch('');
      inputProps.onChange({ target: { value: newValue, name: inputProps.name } });
    }
  };

  const setInputFocus = () => {
    if (!inputRef || !inputRef.current) return;

    inputRef.current.focus();
  };

  const loadOptions = value => {
    const defaultOptionFormatter = item => ({ value: item.id, name: item.number });
    dispatch(loadAction(value))
      .then(data => {
        if (!data) { return; }
        setOptions((data || []).map(optionFormatter || defaultOptionFormatter));
      })
      .catch(response => errorNotification('Загрузка данных', getResponseError(response)));
  };

  const onChangeSearch = event => {
    const { value } = event.target;
    setSearch(value);
    if (!loadAction || !value || value.length < 3) return;
    if (timer) {
      clearTimeout(timer);
    }
    setTimer(setTimeout(() => { loadOptions(value); }, LOADING_DELAY));
  };

  const optionList = [...options, ...defaults.filter(item => !options.find(f => f.value === item.value))];
  const selectedItems = optionList.filter(item => value.includes(item.value));
  const isRequired = (dtoObject
      && dtoObject.validators
      && dtoObject.validators.length
      && !!dtoObject.validators.find(validator => validator.toString() === required.toString()))
    || !!customValidator.find(validator => validator.toString() === required.toString());
  const isAll = optionList.every(o => value.includes(o.value));
  const isSome = optionList.some(o => value.includes(o.value)) && !isAll;
  return (
    <div className={classNames('form-control-wrapper', className)}>
      <Dropdown
        dropBottom
        usePortal
        disabled={disabled}
        setToggleFunction={a => setToggleFunction(() => a)}
      >
        <Dropdown.Toggle className="select-toggle">
          <div
            ref={anchorElement}
            className={classNames('form-control-wrapper multiple-select', className, { material: variant === 'material' }, { default: variant !== 'material' })}
            onClick={setInputFocus}
          >
            <div className={classNames('input-block', { 'is-invalid': isError })}>
              <div className="multiple-value-block">
                {selectedItems.map((item, index) => (
                  <div key={index} className="multiple-value">
                    {item.name}
                    <Icons
                      customIcon
                      className="multiple-value-delete"
                      width={18}
                      height={18}
                      icon="close"
                      onClick={event => {
                        onChange(item);
                        event.preventDefault();
                        event.stopPropagation();
                      }}
                    />
                  </div>
                ))}
                <TextInput inputProps={{ ref: inputRef, onChange: onChangeSearch, value: search }} />
              </div>
              <div htmlFor={props.id} className="form-input-label">
                {renderMessage(label)}{isRequired ? <span className="form-label-mark-required">*</span> : null}
              </div>
              <span className="input-after-block">
                <div className="select-arrow-wrapper">
                  <Icons customIcon className="select-arrow" width={12} height={8} icon="arrow-down" />
                </div>
              </span>
            </div>
            {isError ? <div className="invalid-feedback">{renderMessage(error)}</div> : null}
          </div>
        </Dropdown.Toggle>
        <Dropdown.Menu
          key={optionList}
          disableClickToggle={multiple}
          rightPadding={32}
          className="select-options-wrapper"
          anchorElement={anchorElement}
          classNamePortal="portal-select-options multiple-select-option"
          useResizeContent={false}
        >
          {optionList && optionList.length ? (
            <div className="select-options-list">
              {optionList.map((item, key) => {
                const isSelected = !!value.includes(item.value);
                return (
                  <div
                    key={key}
                    className={classNames('select-option', { selected: isSelected })}
                    onClick={event => {
                      onChange(item);
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                  >
                    <CheckBoxInputWithLabel
                      inputProps={{ value: isSelected, onChange: false }}
                      label={!item.name && item.nameId ? formatMessage({ id: item.nameId }) : item.name || ''}
                    />
                  </div>
                );
              })}
            </div>
          ) : null}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
}
